
import { defineComponent, reactive } from '@vue/runtime-core';
import { ClientConstants, TCM_MAPPING, TOOLTIP_MAPPINGS } from './../client.constants';
import {
  ContentBodySectionModel,
  ContentComponentsModel,
  ContentDetailModel,
  ContentDisclosuresModel
} from './../models/content-model';
import { FractionalCDModel } from './../models/fractional-cd-model';
import FractionalCdSlider from './FractionalCdSlider.vue';
import store from './../store/store';
import { Utils } from './../utils/Utils';
import InputModal from './InputModal.vue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let app: any;

export default defineComponent({
  name: 'ContentComponent',
  components: { FractionalCdSlider, InputModal },
  computed: {
    headerBackground() {
      switch (this.windowWidth > 0) {
        case this.windowWidth > 960:
          return this.headerBackgroundL;
        case this.windowWidth >= 768:
          return this.headerBackgroundM;
        case this.windowWidth >= 375:
          return this.headerBackgroundS;
        default:
          return this.headerBackgroundS;
      }
    },
    contentComponents() {
      return store.contentItems[0]?.components ?? [];
    },
    faqList() {
      const faqComponent = Utils.mapContent(this.contentComponents, TCM_MAPPING.FAQS) || {};
      return faqComponent?.components?.map((component: ContentDetailModel) => component.contentDetail) ?? [];
    },
    apyValuesList() {
      return store.offeringsModel.offerings.map((offering: FractionalCDModel, key: number) => {
        return {
          key: key,
          leftValue: offering.termToMaturity,
          rightValue: offering.sliderRate
        };
      });
    },
    timestampMessage() {
      const timestamp = Utils.formatTimestamp(store.offeringsModel.timestamp);
      return timestamp == '' ? '' : this.clientConstants.TIMESTAMP_MESSAGE.replace('{{ timestamp }}', timestamp);
    }
  },
  data: () => {
    return {
      clientConstants: ClientConstants,
      header: {} as ContentDetailModel,
      windowWidth: window.innerWidth,
      headerBackgroundS: '',
      headerBackgroundM: '',
      headerBackgroundL: '',
      headerSubtext: '',
      fcdTitle: {} as ContentDetailModel,
      fcdImages: [] as string[],
      saveOnYourTermsHeadline: '',
      saveOnYourTermsText: '',
      saveOnYourTermsLink: '',
      sliderIndex: 3,
      store: reactive(store),
      learnMoreText: '',
      learnMoreUrl: '',
      benefits: {} as ContentBodySectionModel,
      risks: {} as ContentBodySectionModel,
      resources: {} as ContentDetailModel,
      additionalLinks: [] as string[],
      cdLaddersUrl: '',
      disclosures: [] as ContentDisclosuresModel[]
    };
  },
  mounted() {
    // Why is this in a next tick?
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });

    this.setHeaderContent();
    this.setSaveOnYourTermsContent();
    this.setIconsContent();
    this.setBenefitsRisksContent();
    this.setAddlResourcesContent();
    this.setDisclosuresContent();

    try {
      Utils.trackingFunction(this.clientConstants.EDU_PAGE);
    } catch (err) {
      console.log('Error In track analytics ' + err);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    sliderChanged(event: Event) {
      this.sliderIndex = Number((event.target as HTMLInputElement).value);
    },
    setHeaderContent() {
      this.header = Utils.mapContent(this.contentComponents, TCM_MAPPING.HEADER_INFO).contentDetail || {};
      this.headerBackgroundS = Utils.processHost(this.header.smallImage?.path, app?.wwwHost);
      this.headerBackgroundM = Utils.processHost(this.header.mediumImage?.path, app?.wwwHost);
      this.headerBackgroundL = Utils.processHost(this.header.largeImage?.path, app?.wwwHost);
      this.headerSubtext = this.header.bodySection?.[0].text ?? '';
    },
    setSaveOnYourTermsContent() {
      const saveOnYourTerms = Utils.mapContent(this.contentComponents, TCM_MAPPING.SAVE).contentDetail || {};
      this.saveOnYourTermsHeadline = saveOnYourTerms?.bodySection?.[0].headline || '';
      this.saveOnYourTermsText = saveOnYourTerms?.bodySection?.[0].text || '';
      this.saveOnYourTermsLink = saveOnYourTerms?.bodySection?.[1].text || '';
    },
    setIconsContent() {
      this.fcdTitle = Utils.mapContent(this.contentComponents, TCM_MAPPING.FCD_ICONS).contentDetail || {};
      this.fcdImages =
        this.fcdTitle.bodySection
          ?.map((icon: ContentBodySectionModel) => {
            return Utils.processImgTag(icon.text, app?.hostUrl);
          })
          .slice(0, 4) ?? [];
    },
    setBenefitsRisksContent() {
      const benefitsRisks = Utils.mapContent(this.contentComponents, TCM_MAPPING.BENEFITS_RISKS).contentDetail || {};
      this.learnMoreText = benefitsRisks?.headline || '';
      this.learnMoreUrl = benefitsRisks?.teaser || '';
      this.benefits = benefitsRisks?.bodySection?.[0] || {};
      this.risks = benefitsRisks?.bodySection?.[1] || {};
    },
    setAddlResourcesContent() {
      this.resources = Utils.mapContent(this.contentComponents, TCM_MAPPING.ADDL_RESOURCES).contentDetail || {};
      this.additionalLinks =
        this.resources.bodySection
          ?.map((link: ContentBodySectionModel) => {
            return Utils.processImgTag(link.text, app?.hostUrl);
          })
          .slice(0, 4) ?? [];
      this.cdLaddersUrl = `${app?.fixedIncomeHost}${app?.fiLandingCdLadders}`;
    },
    setDisclosuresContent() {
      this.disclosures =
        Utils.mapContent(this.contentComponents, TCM_MAPPING.DISCLOSURES).contentDetail?.disclosures || [];
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    openInputModal() {
      (this.$refs['inputModal'] as typeof InputModal).openModal();
    },
    closeInputModal(event: { amount: string; index: number }) {
      this.navigateToScreener(event.amount, event.index);
    },
    navigateToScreener(amount: string, index: number) {
      this.$router.push(`/screener?amount=${Utils.formatNumber(amount)}&duration=${index}`);
    },
    fixScrollbar() {
      // Clearing body style completely puts back the scroll bar when navigating to the screener.
      // However, this is a heavy-handed approach.
      //
      // TODO - do one of the following:
      // 1. (Better) Look for and remove overflow: hidden from style attribute
      // 2. (Best) Figure out how to control the event timing so that force-editing the body style is not necessary
      //
      // Failed attempts and event timing:
      // - changed routes in the update lifecycle hook which happens after render did not remove overflow
      // - using a dummy button and triggering a click event from the close modal method did not remove overflow
      document.body.setAttribute('style', '');
    },
    updateTooltip(content: string) {
      let updatedContent = content;
      TOOLTIP_MAPPINGS.forEach(mapping => {
        const match = this.store.contentItems[0]?.components.find(
          (component: ContentComponentsModel) => component.sourceId === mapping.id
        );
        updatedContent = Utils.fillInTooltip(
          updatedContent,
          mapping.key,
          match?.contentDetail?.headline,
          match?.contentDetail?.text
        );
      });
      return updatedContent;
    }
  },
  watch: {
    $route(to, from) {
      if (to.path === '/screener' && from.path === '/') {
        this.fixScrollbar();
      }
    }
  }
});
